import { currentLang, setCurrentLang, t, applyLang } from './i18n';
import {
  API, currentUser,
  setCachedAllGoals, setCachedEvents, setCachedGoalsJSON, setHasLoadedGoals,
  setUploadFiles, setUserTimezone,
  loadGoalsErrorCount, setLoadGoalsErrorCount, pendingSubtaskGoalIds,
} from './state';
import { api, saveSession, clearSession } from './api';
import { showMsg, hideMsg, classifyPasswordResetHash, classifyConfirmEmailHash } from './helpers';
import { showToast } from './toast';
import { loadAllGoals, setGoalsLoadingOverlay, stopSubtaskPolling } from './upload';
import { requestWorkList, resetWorkListState } from './work-list';
import { showOnboarding } from './profile';
import { handleHashNavigation } from './navigation';
import { getErrorMessage } from './types';
import { classifyInviteAcceptHash } from './invite-accept-logic';
import { setAccountName } from './app-nav';
import type { ResetLinkError } from './components/shared';
import { isSettingsHash } from './settings-nav-logic';

// Recovery proof stays in memory only, never in an application session.
let recoveryTokenHash: string | null = null;
// #2793: a signup confirmation link waits here until the account's password is entered.
let confirmEmailTokenHash: string | null = null;

// #3376: the same rule the server applies; whether a password was leaked is only known there.
function meetsPasswordRule(password: string): boolean {
  return [...password].length >= 12 && new TextEncoder().encode(password).length <= 72;
}

type AuthTab = 'login' | 'signup' | 'reset' | 'update-password';

interface AuthDraft {
  loginEmail: string;
  loginPassword: string;
  loginCode: string;
  resetEmail: string;
  signupName: string;
  signupEmail: string;
  signupPassword: string;
  agreeTerms: boolean;
  newPassword: string;
  newPasswordConfirm: string;
}

const authState = {
  tab: 'login' as AuthTab,
  loginPending: false,
  signupPending: false,
  resetPending: false,
  updatePending: false,
  resetLinkError: null as ResetLinkError | null,
  inviteNotice: false,
  invitedEmail: null as string | null,
  confirmNotice: false,
  signupSentTo: null as string | null,
  resetSentTo: null as string | null,
  // #3377: the password was right and the account asks for the authenticator code next.
  mfaStep: false,
};

export function setAuthInviteNotice(flag: boolean) {
  authState.inviteNotice = flag;
  renderAuthPanel();
  if (flag) void loadInvitedEmail();
}

// #2793: signing up from an invitation uses the invited address, which the
// invitation link proves, so the account is ready without a confirmation mail.
async function loadInvitedEmail() {
  const inviteToken = classifyInviteAcceptHash(window.location.hash);
  if (!inviteToken) return;
  try {
    const data = await api('/api/auth/invitation-target', { method: 'POST', json: { invite_token: inviteToken } });
    authState.invitedEmail = typeof data.email === 'string' ? data.email : null;
  } catch {
    authState.invitedEmail = null;
  }
  renderAuthPanel();
}

let authDraft: AuthDraft = {
  loginEmail: '',
  loginPassword: '',
  loginCode: '',
  resetEmail: '',
  signupName: '',
  signupEmail: '',
  signupPassword: '',
  agreeTerms: false,
  newPassword: '',
  newPasswordConfirm: '',
};

function captureAuthDraft() {
  const textIds: Array<keyof Omit<AuthDraft, 'agreeTerms'>> = [
    'loginEmail', 'loginPassword', 'loginCode', 'resetEmail', 'signupName', 'signupEmail', 'signupPassword', 'newPassword', 'newPasswordConfirm',
  ];
  textIds.forEach((id) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) authDraft[id] = el.value;
  });
  const agreeTerms = document.getElementById('agreeTerms') as HTMLInputElement | null;
  if (agreeTerms) authDraft.agreeTerms = agreeTerms.checked;
}

// The panel normally keeps what is typed; logout passes captureInputs false so the cleared draft wins.
export function renderAuthPanel({ captureInputs = true }: { captureInputs?: boolean } = {}) {
  if (captureInputs) captureAuthDraft();
  const host = document.getElementById('authPanel') as (HTMLElement & {
    state?: typeof authState;
    draft?: AuthDraft;
    requestUpdate?: () => void;
  }) | null;
  if (!host) return;
  host.state = { ...authState };
  host.draft = { ...authDraft };
  host.requestUpdate?.();
}

export function toggleSubtasksCollapse(titleEl: HTMLElement) {
  const toggle = titleEl.querySelector('.subtasks-toggle');
  const body = titleEl.nextElementSibling;
  if (!body || !body.classList.contains('subtasks-body')) return;

  const isCollapsed = body.classList.contains('collapsed');
  if (isCollapsed) {
    body.classList.remove('collapsed');
    toggle.classList.remove('collapsed');
  } else {
    body.classList.add('collapsed');
    toggle.classList.add('collapsed');
  }
}

export function toggleDecisionsCollapse(titleEl: HTMLElement) {
  titleEl.classList.toggle('collapsed');
}

export function switchAuthTab(tab: string) {
  captureAuthDraft();
  if (tab !== 'update-password') recoveryTokenHash = null;
  authState.tab = tab as AuthTab;
  authState.mfaStep = false;
  authDraft.loginCode = '';
  authState.resetLinkError = null;
  authState.signupSentTo = null;
  authState.resetSentTo = null;
  renderAuthPanel();
  if (tab === 'signup') {
    validateSignupForm();
    updatePasswordHints();
  }
  if (tab === 'reset') validateResetForm();
  if (tab === 'update-password') validateUpdatePasswordForm();
}

export async function handleLogin(e: Event) {
  e.preventDefault();
  captureAuthDraft();
  hideMsg('loginError');
  authState.loginPending = true;
  renderAuthPanel();
  try {
    const confirming = confirmEmailTokenHash !== null;
    const data = await api(confirming ? '/api/auth/confirm-email' : '/api/auth/login', {
      method: 'POST',
      signIn: true,
      json: {
        email: authDraft.loginEmail,
        password: authDraft.loginPassword,
        ...(confirming ? { token_hash: confirmEmailTokenHash } : {}),
        ...(!confirming && authState.mfaStep ? { mfa_code: authDraft.loginCode } : {}),
      }
    });
    if (confirming) {
      confirmEmailTokenHash = null;
      authState.confirmNotice = false;
      showToast(t('confirmEmailDone') as string, 'success');
    }
    authState.mfaStep = false;
    authDraft.loginCode = '';
    saveSession(data.user || { email: authDraft.loginEmail });
    enterApp();
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    if (code === 'mfa_required' || code === 'mfa_invalid') {
      // #3377: keep the email and password; the next submit sends them with the code.
      const firstAsk = !authState.mfaStep;
      authState.mfaStep = true;
      authDraft.loginCode = '';
      if (!firstAsk || code === 'mfa_invalid') showMsg('loginError', getErrorMessage(err));
    } else {
      showMsg('loginError', getErrorMessage(err));
    }
  } finally {
    authState.loginPending = false;
    renderAuthPanel();
    if (authState.mfaStep) requestAnimationFrame(() => (document.getElementById('loginCode') as HTMLInputElement | null)?.focus());
  }
  return false;
}

export function updatePasswordHints() {
  const pw = (document.getElementById('signupPassword') as HTMLInputElement).value;
  const hints = document.getElementById('passwordHints') as HTMLElement;
  hints.style.display = pw ? '' : 'none';
  const rules: [string, boolean][] = [
    ['pwHintLength', meetsPasswordRule(pw)],
  ];
  rules.forEach(([id, ok]) => {
    const el = document.getElementById(id as string);
    const label = t(el.getAttribute('data-i18n-hint') as string);
    el.textContent = (ok ? '✓ ' : '・') + label;
    el.style.color = ok ? 'var(--success, #2e7d32)' : 'var(--text-muted)';
  });
}

export function renderAgreeTermsText() {
  // Compatibility shim for i18n.ts. Link rendering lives in the Lit auth component.
  renderAuthPanel();
}

export function validateResetForm() {
  const email = (document.getElementById('resetEmail') as HTMLInputElement).value.trim();
  (document.getElementById('resetBtn') as HTMLButtonElement).disabled = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validateUpdatePasswordForm() {
  const pw = (document.getElementById('newPassword') as HTMLInputElement).value;
  const confirm = (document.getElementById('newPasswordConfirm') as HTMLInputElement).value;
  const hints = document.getElementById('updatePwHints') as HTMLElement;
  hints.style.display = pw ? '' : 'none';
  const rules: [string, boolean][] = [
    ['updatePwHintLength', meetsPasswordRule(pw)],
    ['updatePwHintMatch', !!(pw && confirm && pw === confirm)],
  ];
  let allOk = true;
  rules.forEach(([id, ok]) => {
    const el = document.getElementById(id as string);
    const label = t(el.getAttribute('data-i18n-hint') as string);
    el.textContent = (ok ? '✓ ' : '・') + label;
    el.style.color = ok ? 'var(--success, #2e7d32)' : 'var(--text-muted)';
    if (!ok) allOk = false;
  });
  (document.getElementById('updatePasswordBtn') as HTMLButtonElement).disabled = !allOk;
}

export function validateSignupForm() {
  const name = (document.getElementById('signupName') as HTMLInputElement).value.trim();
  const email = (document.getElementById('signupEmail') as HTMLInputElement).value.trim();
  const pw = (document.getElementById('signupPassword') as HTMLInputElement).value;
  const agreed = (document.getElementById('agreeTerms') as HTMLInputElement).checked;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwValid = meetsPasswordRule(pw);
  (document.getElementById('signupBtn') as HTMLButtonElement).disabled = !(name && emailValid && pwValid && agreed);
}

export async function handleSignup(e: Event) {
  e.preventDefault();
  captureAuthDraft();
  const btn = document.getElementById('signupBtn') as HTMLButtonElement;
  if (btn.disabled) return false;
  hideMsg('signupError');
  const signupPassword = authDraft.signupPassword;
  if (!meetsPasswordRule(signupPassword)) {
    showMsg('signupError', t('signupPasswordError'));
    return false;
  }
  if (!authDraft.agreeTerms) {
    showMsg('signupError', t('signupValidationTerms'));
    return false;
  }
  authState.signupPending = true;
  renderAuthPanel();
  try {
    const data = await api('/api/auth/signup', {
      method: 'POST',
      json: {
        name: authDraft.signupName,
        email: authState.invitedEmail ?? authDraft.signupEmail,
        password: signupPassword,
        // #2793: an invitation link for this address confirms it without a second mail.
        invite_token: classifyInviteAcceptHash(window.location.hash) ?? undefined,
      }
    });
    if (data.pending_confirmation) {
      authState.signupSentTo = authState.invitedEmail ?? authDraft.signupEmail;
      authDraft.signupPassword = '';
      return false;
    }
    saveSession(data.user || { email: authDraft.signupEmail, name: authDraft.signupName });
    enterApp();
  } catch (err: unknown) {
    showMsg('signupError', getErrorMessage(err));
  } finally {
    authState.signupPending = false;
    renderAuthPanel();
    validateSignupForm();
  }
  return false;
}

export async function handleResetPassword(e: Event) {
  e.preventDefault();
  captureAuthDraft();
  const email = authDraft.resetEmail;
  authState.resetPending = true;
  authState.resetLinkError = null;
  renderAuthPanel();
  document.getElementById('resetError').classList.remove('show');
  try {
    await api('/api/auth/reset-password', {
      method: 'POST',
      json: { email }
    });
    authState.resetSentTo = email;
  } catch (err: unknown) {
    showMsg('resetError', getErrorMessage(err));
  } finally {
    authState.resetPending = false;
    renderAuthPanel();
  }
  return false;
}

export async function handleUpdatePassword(e: Event) {
  e.preventDefault();
  captureAuthDraft();
  const password = authDraft.newPassword;
  const confirm = authDraft.newPasswordConfirm;
  document.getElementById('updatePasswordError').classList.remove('show');
  document.getElementById('updatePasswordSuccess').classList.remove('show');

  if (password !== confirm) {
    showMsg('updatePasswordError', t('passwordMismatch'));
    return false;
  }
  if (!meetsPasswordRule(password)) {
    showMsg('updatePasswordError', t('signupPasswordError'));
    return false;
  }

  authState.updatePending = true;
  renderAuthPanel();
  try {
    const tokenHash = recoveryTokenHash;
    recoveryTokenHash = null;
    if (!tokenHash) throw new Error(t('resetLinkInvalid') as string);
    await api('/api/auth/recover-password', {
      method: 'POST',
      json: { password, token_hash: tokenHash }
    });
    const successEl = document.getElementById('updatePasswordSuccess');
    successEl.textContent = t('updatePasswordSuccessMsg') as string;
    successEl.classList.add('show');
    setTimeout(() => {
      clearSession();
      switchAuthTab('login');
    }, 2000);
  } catch (err: unknown) {
    showMsg('updatePasswordError', getErrorMessage(err));
  } finally {
    authState.updatePending = false;
    renderAuthPanel();
  }
  return false;
}

export async function enterApp() {
  document.getElementById('authScreen').style.display = 'none';
  document.getElementById('appScreen').style.display = 'block';
  document.getElementById('userInfo').style.display = 'flex';
  document.getElementById('appRail')?.removeAttribute('hidden');
  setAccountName(currentUser.name || currentUser.email || '');
  setGoalsLoadingOverlay(true);

  // The first tab draws from this list, so it is asked for alongside
  // /api/profile. The onboarding path drops this response; a failure is
  // reported by loadAllGoals, which reads the goals after it (#3341).
  const workListRequest = requestWorkList();
  workListRequest.catch(() => {});

  try {
    const data = await api('/api/profile');
    if (!data.profile || data.profile.sync_rules == null) {
      showOnboarding();
      return;
    }
    if (data.profile?.preferences?.timezone) {
      setUserTimezone(data.profile.preferences.timezone);
    }
    if (data.profile?.language) {
      setCurrentLang(data.profile.language);
      localStorage.setItem('zenmetry_lang', currentLang);
      applyLang();
    }
  } catch {}

  await loadAllGoals({ workListRequest });

  document.getElementById('createFab').style.display = 'flex';

  handleHashNavigation();

  startGoalRefresh();
}

// Every sign-in passes through enterApp, so the refresh is set up only once,
// and it does not read anything while nobody is signed in.
let goalRefreshStarted = false;
// A goal finished in the Chrome extension's side panel leaves the tab visible
// the whole time, so coming back to the page is noticed by its focus, not by
// visibilitychange. Switching tabs fires both; a refresh started less than
// this long ago covers the other one.
const RETURN_REFRESH_GAP_MS = 10000;
let lastGoalRefreshAt = 0;
function refreshGoals() {
  if (!currentUser || document.visibilityState !== 'visible') return;
  lastGoalRefreshAt = Date.now();
  loadAllGoals({ silent: true, poll: true });
}
function refreshGoalsOnReturn() {
  if (Date.now() - lastGoalRefreshAt < RETURN_REFRESH_GAP_MS) return;
  refreshGoals();
}
function startGoalRefresh() {
  if (goalRefreshStarted) return;
  goalRefreshStarted = true;
  document.addEventListener('visibilitychange', refreshGoalsOnReturn);
  window.addEventListener('focus', refreshGoalsOnReturn);
  setInterval(() => {
    if (loadGoalsErrorCount < 3) refreshGoals();
  }, 30000);
}

// The settings page covers the auth screen, so a signed-out page leaves #settings.
// The session is cleared first, so the settings page closes without asking about unsaved edits.
export function leaveSettingsWhenSignedOut() {
  if (!isSettingsHash(window.location.hash)) return;
  history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  window.dispatchEvent(new Event('hashchange'));
}

export function logout() {
  fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  clearSession();
  leaveSettingsWhenSignedOut();
  setCachedAllGoals([]);
  setCachedGoalsJSON(null);
  setHasLoadedGoals(false);
  setLoadGoalsErrorCount(0);
  stopSubtaskPolling();
  pendingSubtaskGoalIds.clear();
  setCachedEvents([]);
  resetWorkListState();

  setUploadFiles([]);
  document.getElementById('authScreen').style.display = '';
  document.getElementById('appScreen').style.display = 'none';
  document.getElementById('userInfo').style.display = 'none';
  document.getElementById('appRail')?.setAttribute('hidden', '');
  document.getElementById('createFab').style.display = 'none';
  authState.tab = 'login';
  authState.mfaStep = false;
  // Logging out to switch to the invited account keeps the invitation link in the address bar.
  authState.inviteNotice = classifyInviteAcceptHash(window.location.hash) !== null;
  authState.invitedEmail = null;
  authState.confirmNotice = false;
  confirmEmailTokenHash = null;
  setCurrentLang(navigator.language.startsWith('en') ? 'en' : 'ja');
  localStorage.setItem('zenmetry_lang', currentLang);
  applyLang();
  // applyLang re-renders the panel, which reads the typed values back, so clear the draft after it.
  authDraft = {
    loginEmail: '',
    loginPassword: '',
    loginCode: '',
    resetEmail: '',
    signupName: '',
    signupEmail: '',
    signupPassword: '',
    agreeTerms: false,
    newPassword: '',
    newPasswordConfirm: '',
  };
  renderAuthPanel({ captureInputs: false });
  if (authState.inviteNotice) void loadInvitedEmail();
}

export async function handleConfirmEmailHash() {
  const confirmHash = classifyConfirmEmailHash(window.location.hash);
  if (!confirmHash) return false;
  history.replaceState(null, '', window.location.pathname);
  await customElements.whenDefined('zen-auth-panel');
  const panel = document.getElementById('authPanel') as (HTMLElement & { updateComplete?: Promise<unknown> }) | null;
  await panel?.updateComplete;
  // Never act on someone else's session: the link is finished on the login form.
  clearSession();
  switchAuthTab('login');
  if (confirmHash.kind === 'signup') {
    confirmEmailTokenHash = confirmHash.tokenHash;
    // renderAuthPanel reads the draft back from the inputs, so fill the input itself.
    const emailInput = document.getElementById('loginEmail') as HTMLInputElement | null;
    if (emailInput) emailInput.value = confirmHash.email;
    authState.confirmNotice = true;
    renderAuthPanel();
    return true;
  }
  showMsg('loginError', t('confirmEmailInvalid'));
  return true;
}

export async function handlePasswordResetHash() {
  const resetHash = classifyPasswordResetHash(window.location.hash);
  if (!resetHash) return false;
  history.replaceState(null, '', window.location.pathname);
  recoveryTokenHash = null;
  // Auth controls are registered asynchronously on a cold page load.
  await customElements.whenDefined('zen-auth-panel');
  const panel = document.getElementById('authPanel') as (HTMLElement & { updateComplete?: Promise<unknown> }) | null;
  await panel?.updateComplete;
  const error: ResetLinkError = 'invalid';
  if (resetHash.kind === 'recovery') {
    recoveryTokenHash = resetHash.tokenHash;
    clearSession();
    switchAuthTab('update-password');
    return true;
  }
  // Never restore a session from a link that did not grant recovery authority.
  switchAuthTab('reset');
  authState.resetLinkError = error;
  renderAuthPanel();
  return true;
}
